import { useCallback, useEffect, useRef, useState } from 'react';

type ScrollFade = { left: boolean; right: boolean };

const useHorizontalScrollFade = (fadeSize = 44) => {
  const ref = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState<ScrollFade>({ left: false, right: false });

  const update = useCallback(() => {
    const scroller = ref.current;
    if (!scroller) return;
    const { scrollLeft, scrollWidth, clientWidth } = scroller;
    const nextFade = {
      left: scrollLeft > 1,
      right: scrollLeft + clientWidth < scrollWidth - 1,
    };
    setFade((previousFade) =>
      previousFade.left === nextFade.left && previousFade.right === nextFade.right
        ? previousFade
        : nextFade,
    );
  }, []);

  useEffect(() => {
    const scroller = ref.current;
    if (!scroller) return undefined;
    update();
    scroller.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(scroller);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(scroller, { childList: true, subtree: true });
    return () => {
      scroller.removeEventListener('scroll', update);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [update]);

  const maskImage =
    fade.left || fade.right
      ? `linear-gradient(to right, ${
          fade.left
            ? `transparent 0, var(--chakra-colors-black) ${fadeSize}px`
            : 'var(--chakra-colors-black) 0'
        }, ${
          fade.right
            ? `var(--chakra-colors-black) calc(100% - ${fadeSize}px), transparent 100%`
            : 'var(--chakra-colors-black) 100%'
        })`
      : undefined;

  return { ref, maskImage, fade };
};

export default useHorizontalScrollFade;
